"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFeedStore = createFeedStore;
const zustand_1 = require("zustand");
const middleware_1 = require("zustand/middleware");
const DEFAULT_QUERY = { page: 1, filter: null, q: '', sort: 'top' };
const SORTS = ['top', 'new'];
/**
 * @param filters 可選的篩選值（不含「全部」的 null）：記住的篩選不在清單裡（改版後選項變了）時回到「全部」
 */
function createFeedStore(name, fetcher, defaults = {}, { filters }) {
    const initialQuery = { ...DEFAULT_QUERY, ...defaults };
    const isFirstPage = (q) => q.page === 1 && !q.q;
    let requestId = 0;
    return (0, zustand_1.create)()((0, middleware_1.persist)((set, get) => ({
        data: null,
        cached: null,
        cachedFor: null,
        query: initialQuery,
        isFetching: false,
        error: false,
        fetch: async (changes = {}, { silent = false } = {}) => {
            const query = { ...get().query, ...changes };
            const id = ++requestId; // 打字搜尋時只採用最後一次的結果
            set({ query, isFetching: !silent, error: false });
            try {
                const data = await fetcher(query);
                if (id !== requestId)
                    return;
                // 頁碼超出範圍（例：最後一頁唯一一則被刪掉）：跳到最後一頁，不停在空白頁
                if (data.posts.length === 0 && query.page > 1 && query.page > data.totalPages) {
                    return get().fetch({ page: data.totalPages }, { silent });
                }
                set({ data, isFetching: false, ...(isFirstPage(query) && { cached: data, cachedFor: { filter: query.filter, sort: query.sort } }) });
            }
            catch {
                if (id === requestId)
                    set({ isFetching: false, error: true });
            }
        },
        open: () => {
            const { filter, sort } = get().query;
            const { cached, cachedFor } = get();
            const matches = cachedFor?.filter === filter && cachedFor?.sort === sort;
            set({ data: matches ? cached : null });
            // 有快取就靜默重抓（正式站 API 要幾百毫秒，變淡會像整塊暗掉）；沒快取時畫面是骨架，不受影響
            return get().fetch({ page: 1, q: '', filter, sort }, { silent: true });
        },
        patch: (id, changes) => set(state => state.data
            ? { data: { ...state.data, posts: state.data.posts.map(p => (p.id === id ? { ...p, ...changes } : p)) } }
            : state),
        prepend: post => set(state => state.data
            ? { data: { ...state.data, posts: [post, ...state.data.posts], total: state.data.total + 1 } }
            : state),
        // 樂觀移除；刪除 API 成功後頁面要呼叫 fetch() 重抓（更新筆數；這頁空了會自動跳到最後一頁）
        remove: id => set(state => state.data
            ? { data: { ...state.data, posts: state.data.posts.filter(p => p.id !== id), total: Math.max(0, state.data.total - 1) } }
            : state),
    }), {
        name,
        // 只保存篩選、排序與它的第一頁；搜尋、頁碼不記住
        partialize: state => ({
            cached: state.cached,
            cachedFor: state.cachedFor,
            query: { filter: state.query.filter, sort: state.query.sort },
        }),
        merge: (persisted, current) => {
            const saved = (persisted ?? {});
            const filter = saved.query?.filter && filters.includes(saved.query.filter) ? saved.query.filter : initialQuery.filter;
            const sort = saved.query?.sort && SORTS.includes(saved.query.sort) ? saved.query.sort : initialQuery.sort;
            // 快取和條件是否相符由 open() 判斷
            return { ...current, query: { ...initialQuery, filter, sort }, cached: saved.cached ?? null, cachedFor: saved.cachedFor ?? null };
        },
    }));
}
